import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { Icon } from '@components/icon/Icon';
import { useFormContext, useWatch } from 'react-hook-form';
import { SettingsOutletHeader } from '../../shared/SettingsOutletHeader';
import { type AdminRestaurant } from '../../team/apiHelpers';
import { type GeneralSettingsForm } from '../utils/generalSettingsFormTypes';
import layoutStyles from './CoverLimitSection.scss';

const COVER_LIMIT_SECTION_EXPLANATION =
  'Set the cover limit for your entire restaurant. This setting will make sure that at any given moment, the number of guests in your restaurant does not exceed the set limit.';

export interface CoverLimitSectionProps {
  restaurant: AdminRestaurant;
  sectionTitleId: string;
  isSaving: boolean;
  onRequestRemoveCoverLimit: () => void;
}

export const CoverLimitSection = ({
  restaurant,
  sectionTitleId,
  isSaving,
  onRequestRemoveCoverLimit,
}: CoverLimitSectionProps) => {
  const { control } = useFormContext<GeneralSettingsForm>();
  const watchedCoverLimit = useWatch({ control, name: 'coverLimit' });
  const showRemoveCoverLimitButton =
    restaurant.coverLimit != null &&
    String(watchedCoverLimit ?? '').trim() !== '';

  return (
    <section className={layoutStyles.section} aria-labelledby={sectionTitleId}>
      <SettingsOutletHeader
        variant="section"
        titleId={sectionTitleId}
        title="Cover Limit"
        subtitle={COVER_LIMIT_SECTION_EXPLANATION}
      />
      <div className={layoutStyles.formFields}>
        <ControlledFormInput
          control={control}
          label="Cover limit"
          name="coverLimit"
          rules={{
            validate: (value) => {
              const trimmed = String(value ?? '').trim();
              if (trimmed === '') {
                return true;
              }
              const n = Number(trimmed);
              if (Number.isNaN(n)) {
                return 'Enter a valid number or leave blank for no limit';
              }
              if (n < 1) {
                return 'Must be at least 1';
              }
              return true;
            },
          }}
          type="number"
          variant={LayoutVariant.Vertical}
          className={layoutStyles.constrainedField}
        />
        {showRemoveCoverLimitButton ? (
          <button
            type="button"
            className={layoutStyles.removeButton}
            disabled={isSaving}
            aria-label="Remove cover limit"
            onClick={onRequestRemoveCoverLimit}
          >
            <Icon ariaLabel="Remove cover limit" name="trash" />
            <span className={layoutStyles.removeLabel}>Remove Cover Limit</span>
          </button>
        ) : null}
      </div>
    </section>
  );
};
